import type { WidgetConfig } from '@/types/config';
import { getDomainConfig } from '@/types/content';
import { Sanitizer } from '@/security/Sanitizer';

/**
 * Builds URLs for embedding content based on content type
 */
export class UrlBuilder {
  /**
   * Generate embed widget URL
   * @param config - Widget configuration
   * @returns Complete URL for iframe src
   */
  static generateUrl(config: WidgetConfig): string {
    const redirectUrl = `&redirecturl=${encodeURIComponent(window.location.href)}`;

    switch (config.contentType) {
      case 'cd-searchsite':
        return this.buildSearchSiteUrl(config, redirectUrl);
      case 'cd-form':
        return this.buildFormUrl(config, redirectUrl);
      case 'cd-routing-form':
        return this.buildRoutingFormUrl(config, redirectUrl);
      case 'cd-service':
        return this.buildServiceUrl(config, redirectUrl);
      case 'cd-request':
        return this.buildRequestUrl(config, redirectUrl);
      case 'url':
        return this.buildCustomUrl(config, redirectUrl);
      case 'url-element':
        return this.buildElementUrl(config, redirectUrl);
      default:
        throw new Error(`[CaredoveWidget] Unsupported content type: ${config.contentType}`);
    }
  }

  /**
   * Build search site URL
   * Format: https://search[.subdomain].caredove.com/{contentId}?params
   */
  private static buildSearchSiteUrl(config: WidgetConfig, redirectUrl: string): string {
    const domain = getDomainConfig(config.subdomain);
    const subdomain = config.subdomain && config.subdomain !== 'prod' ? `${config.subdomain}.` : '';

    let params = this.buildBaseParams(config);

    // Add mode parameter based on widget mode
    if (!config.parameters?.includes('mode=')) {
      if (config.mode === 'inline' || config.mode === 'inline-simple') {
        params += '&mode=search';
      } else if (config.mode === 'overlay') {
        params += '&mode=chromeless';
      }
    }

    // Append custom parameters if provided
    if (config.parameters) {
      params += `&${config.parameters}`;
    }

    const url = `${domain.protocol}${domain.searchPrefix}${subdomain}${domain.caredoveDomain}/${config.contentId}${params}${redirectUrl}`;

    return Sanitizer.sanitizeUrl(url);
  }

  /**
   * Build form URL
   * Format: https://assessment[.subdomain].caredove.com/form/[caredove/]{contentId}
   */
  private static buildFormUrl(config: WidgetConfig, redirectUrl: string): string {
    const domain = getDomainConfig(config.subdomain);
    const subdomain = config.subdomain && config.subdomain !== 'prod' ? `${config.subdomain}.` : '';

    // Determine folder structure
    const contentIdParts = config.contentId.split('/');
    const folderName = contentIdParts.length > 1 ? '' : 'caredove/';

    let params = '';
    if (config.mode === 'inline' || config.mode === 'inline-simple') {
      params = config.contentId.includes('?') ? '&mode=chromeless' : '?mode=chromeless';
    }

    const url = `${domain.protocol}${domain.formsPrefix}${subdomain}${domain.caredoveDomain}/form/${folderName}${config.contentId}${params}${redirectUrl}`;

    return Sanitizer.sanitizeUrl(url);
  }

  /**
   * Build routing form URL
   * Format: https://form.caredove[.dev/.io].com/deploy/{contentId}
   */
  private static buildRoutingFormUrl(config: WidgetConfig, redirectUrl: string): string {
    let baseUrl = 'https://form.caredove.com/';

    if (config.subdomain === 'dev') {
      baseUrl = 'https://form.caredove.dev/';
    } else if (config.subdomain === 'stage') {
      baseUrl = 'https://form.caredove.io/';
    }

    let params = '';
    if (config.mode === 'inline' || config.mode === 'inline-simple') {
      params = config.contentId.includes('?') ? '&mode=chromeless' : '?mode=chromeless';
    }

    const url = `${baseUrl}deploy/${config.contentId}${params}${redirectUrl}`;

    return Sanitizer.sanitizeUrl(url);
  }

  /**
   * Build service URL
   * Format: https://search[.subdomain].caredove.com/[searchsite/]service/{serviceId}
   */
  private static buildServiceUrl(config: WidgetConfig, redirectUrl: string): string {
    const domain = getDomainConfig(config.subdomain);
    const subdomain = config.subdomain && config.subdomain !== 'prod' ? `${config.subdomain}.` : '';

    // Parse service ID (could be "{serviceId}" or "{searchsite}/service/{serviceId}")
    const parts = config.contentId.split('/');
    let serviceUrl = `${domain.protocol}${domain.searchPrefix}${subdomain}${domain.caredoveDomain}`;

    if (parts.length > 2) {
      // Format: {searchsite}/service/{serviceId}
      serviceUrl += `/${parts[0]}/service/${parts[2]}`;
    } else {
      // Format: {serviceId}
      serviceUrl += `/service/${parts[0]}`;
    }

    // Add embed parameter
    const embedParam = config.contentId.includes('?') ? '&embed=1' : '?embed=1';
    serviceUrl += `${embedParam}&embedmode=${config.mode}`;

    // Add mode parameter
    if (!config.parameters?.includes('mode=')) {
      if (config.mode === 'inline' || config.mode === 'inline-simple') {
        serviceUrl += '&mode=search';
      } else if (config.mode === 'overlay') {
        serviceUrl += '&mode=chromeless';
      }
    }

    serviceUrl += redirectUrl;

    return Sanitizer.sanitizeUrl(serviceUrl);
  }

  /**
   * Build service request URL
   * Format: https://search[.subdomain].caredove.com/[searchsite/]service/{serviceId}/request
   */
  private static buildRequestUrl(config: WidgetConfig, redirectUrl: string): string {
    const domain = getDomainConfig(config.subdomain);
    const subdomain = config.subdomain && config.subdomain !== 'prod' ? `${config.subdomain}.` : '';

    // Parse service ID
    const parts = config.contentId.split('/');
    let requestUrl = `${domain.protocol}${domain.searchPrefix}${subdomain}${domain.caredoveDomain}`;

    if (parts.length > 2 && parts[2]) {
      // Format: {searchsite}/service/{serviceId}
      const serviceIdAndParams = parts[2].split('?');
      const serviceId = serviceIdAndParams[0];
      const queryParams = serviceIdAndParams[1];

      requestUrl += `/${parts[0]}/service/${serviceId}/request`;
      requestUrl += queryParams ? `?${queryParams}&embed=1` : '?embed=1';
    } else if (parts[0]) {
      // Format: {serviceId}
      const serviceIdAndParams = parts[0].split('?');
      const serviceId = serviceIdAndParams[0];
      const queryParams = serviceIdAndParams[1];

      requestUrl += `/service/${serviceId}/request`;
      requestUrl += queryParams ? `?${queryParams}&embed=1` : '?embed=1';
    }

    requestUrl += `&embedmode=${config.mode}`;

    // Add mode parameter
    if (!config.parameters?.includes('mode=')) {
      if (config.mode === 'inline' || config.mode === 'inline-simple') {
        requestUrl += '&mode=search';
      } else if (config.mode === 'overlay') {
        requestUrl += '&mode=chromeless';
      }
    }

    requestUrl += redirectUrl;

    return Sanitizer.sanitizeUrl(requestUrl);
  }

  /**
   * Build custom URL
   * Format: {contentId}?embed=1&params
   */
  private static buildCustomUrl(config: WidgetConfig, redirectUrl: string): string {
    const embedParam = config.contentId.includes('?') ? '&embed=1' : '?embed=1';
    let url = config.contentId + embedParam;

    // Add custom parameters if provided
    if (config.parameters) {
      url += `&${config.parameters}`;
    }

    url += redirectUrl;

    return Sanitizer.sanitizeUrl(url);
  }

  /**
   * Build URL from DOM element value
   * Format: value of element with id={contentId}
   */
  private static buildElementUrl(config: WidgetConfig, redirectUrl: string): string {
    const element = document.getElementById(config.contentId) as HTMLInputElement | null;

    if (!element) {
      throw new Error(`[CaredoveWidget] Element with id "${config.contentId}" not found`);
    }

    const url = element.value || element.textContent || '';

    if (!url) {
      throw new Error(`[CaredoveWidget] Element "${config.contentId}" has no value`);
    }

    return Sanitizer.sanitizeUrl(url + redirectUrl);
  }

  /**
   * Build base parameters for embed mode
   */
  private static buildBaseParams(config: WidgetConfig): string {
    const hasQueryParams = config.contentId.includes('?');
    return hasQueryParams ? `&embedmode=${config.mode}` : `?embedmode=${config.mode}`;
  }
}
